import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EngoLogo } from "../../components/brand/EngoLogo.js";
import { GlassCard } from "../../components/ui/GlassCard.js";
import { GradientButton } from "../../components/ui/GradientButton.js";
import { durations, easing } from "../../lib/motion.js";

const SLIDES = [
  { title: "onboarding.slide1Title", body: "onboarding.slide1Body" },
  { title: "onboarding.slide2Title", body: "onboarding.slide2Body" },
  { title: "onboarding.slide3Title", body: "onboarding.slide3Body" },
];
const FALLBACK_SLIDE = SLIDES[0]!;

export function Onboarding({ onComplete }: { onComplete: () => void }) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const isLast = index === SLIDES.length - 1;
  const slide = SLIDES[index] ?? FALLBACK_SLIDE;

  return (
    <GlassCard className="text-center">
      <div className="flex flex-col items-center gap-3">
        <EngoLogo size={48} />
      </div>

      <div className="mt-6 min-h-[140px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={reduceMotion ? false : { opacity: 0, x: 16 }}
            animate={reduceMotion ? false : { opacity: 1, x: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, x: -16 }}
            transition={{ duration: durations.base, ease: easing }}
          >
            <h2 className="font-display text-xl font-semibold text-[var(--text-primary)]">
              {t(slide.title)}
            </h2>
            <p className="mt-3 text-sm text-[var(--text-secondary)]">{t(slide.body)}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-6 flex items-center justify-center gap-2">
        {SLIDES.map((s, i) => (
          <span
            key={s.title}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-6 bg-[var(--primary)]" : "w-1.5 bg-[var(--glass-border-strong)]"
            }`}
            aria-hidden
          />
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onComplete}
          className="text-sm text-[var(--text-muted)] transition-colors hover:text-[var(--text-secondary)]"
        >
          {t("onboarding.skip")}
        </button>
        <GradientButton
          onClick={() => (isLast ? onComplete() : setIndex((i) => i + 1))}
          className="px-6 py-2.5"
        >
          {isLast ? t("onboarding.getStarted") : t("onboarding.next")}
        </GradientButton>
      </div>
    </GlassCard>
  );
}
